import { getPortalSupabase } from "@/lib/portalSupabase";
import { getSupabase } from "@/lib/supabase";
import { fetchWithAuth } from "@/lib/api";

export const isPortalPreview = () => localStorage.getItem("portal_preview") === "true";

async function getPortalHeaders(): Promise<HeadersInit> {
  // Staff preview mode uses the staff session token; the server enforces read-only.
  const supabase = isPortalPreview() ? await getSupabase() : await getPortalSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const accountId = localStorage.getItem("portal_account_id");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(accountId ? { "X-Portal-Account-Id": accountId } : {}),
  };
}

async function portalFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getPortalHeaders();
  const res = await fetch(path, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export type PortalMe = {
  email: string;
  account_id: string;
  account_name: string;
};

export type PortalProject = {
  id: string;
  title: string;
  status: string;
  due_date: string | null;
  task_count: number;
  completed_task_count: number;
  period?: string | null; // display-ready accounting period, e.g. "Jul 2026" — render verbatim, never parse
};

export type PortalProjectDetail = {
  id: string;
  title: string;
  status: string;
  due_date: string | null;
  description: string | null;
  period?: string | null; // display-ready accounting period, e.g. "Jul 2026" — render verbatim, never parse
};

export type PortalTask = {
  id: string;
  title: string;
  status: string;
  due_date: string | null;
  allow_client_upload: boolean;
  completed_at: string | null;
  firm_attachment_count?: number; // files staff shared on this task (ENG-60)
};

export type PortalComment = {
  id: string;
  body: string;
  created_at: string;
  author_name: string | null; // staff name, or null for the client's own comments
};

export type PortalDocument = {
  id: string;
  folder_path: string;
  file_name: string;
  file_size: number | null;
  content_type: string | null;
  notes: string | null;
  uploaded_at: string;
};

export type PortalProjectMessage = {
  id: string;
  author_name: string | null;
  author_portal_email: string | null;
  body: string;
  is_client_visible: true;
  created_at: string;
};

export type PortalClientTopic = {
  id: string;
  account_id: string;
  name: string;
  created_at: string;
};

export type PortalClientTopicMessage = {
  id: string;
  topic_id: string;
  author_user_id: string | null;
  author_portal_email: string | null;
  author_name: string;
  body: string;
  channel: string;
  created_at: string;
  // null when never edited
  edited_at?: string | null;
  // absent on post-a-message response
  likeCount?: number;
  likedByMe?: boolean;
  likers?: string[];
};

export type PortalAttachment = {
  id: string;
  name: string;
  file_size: number | null;
  file_type: string | null;
  created_at: string;
  from_firm?: boolean;
  uploader_name?: string | null;
};

export type PortalInvite = {
  id: string;
  email: string;
  invited_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
};

export type PortalAccount = {
  account_id: string;
  account_name: string;
};

export const portalApi = {
  acceptInvite: () =>
    portalFetch<{ ok: boolean }>("/api/portal/auth/accept", { method: "POST" }),

  accounts: () => portalFetch<PortalAccount[]>("/api/portal/accounts"),

  me: () => portalFetch<PortalMe>("/api/portal/me"),

  projects: () => portalFetch<PortalProject[]>("/api/portal/projects"),

  project: (id: string) => portalFetch<PortalProjectDetail>(`/api/portal/projects/${id}`),

  tasks: (projectId: string) => portalFetch<PortalTask[]>(`/api/portal/projects/${projectId}/tasks`),

  comments: (taskId: string) => portalFetch<PortalComment[]>(`/api/portal/tasks/${taskId}/comments`),

  attachments: (taskId: string) => portalFetch<PortalAttachment[]>(`/api/portal/tasks/${taskId}/attachments`),

  registerAttachment: (taskId: string, payload: { name: string; file_path: string; file_size?: number; file_type?: string }) =>
    portalFetch<PortalAttachment>(`/api/portal/tasks/${taskId}/attachments`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  postComment: (taskId: string, body: string) =>
    portalFetch<PortalComment>(`/api/portal/tasks/${taskId}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),

  completeTask: (taskId: string, completed: boolean) =>
    portalFetch<PortalTask>(`/api/portal/tasks/${taskId}/complete`, {
      method: "POST",
      body: JSON.stringify({ completed }),
    }),

  downloadUrl: (taskId: string, attachmentId: string, mode?: "view" | "download") =>
    portalFetch<{ url: string }>(`/api/portal/tasks/${taskId}/attachments/${attachmentId}/download${mode === "view" ? "?mode=view" : ""}`),

  projectMessages: (projectId: string) =>
    portalFetch<PortalProjectMessage[]>(`/api/portal/projects/${projectId}/messages`),

  postProjectMessage: (projectId: string, body: string) =>
    portalFetch<PortalProjectMessage>(`/api/portal/projects/${projectId}/messages`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),

  accountClientTopics: () =>
    portalFetch<PortalClientTopic[]>("/api/portal/client-topics"),

  clientTopics: (projectId: string) =>
    portalFetch<PortalClientTopic[]>(`/api/portal/projects/${projectId}/client-topics`),

  clientTopicMessages: (topicId: string) =>
    portalFetch<PortalClientTopicMessage[]>(`/api/portal/client-topics/${topicId}/messages`),

  postClientTopicMessage: (topicId: string, body: string) =>
    portalFetch<PortalClientTopicMessage>(`/api/portal/client-topics/${topicId}/messages`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),

  updateClientTopicMessage: (topicId: string, messageId: string, body: string) =>
    portalFetch<PortalClientTopicMessage>(`/api/portal/client-topics/${topicId}/messages/${messageId}`, {
      method: "PATCH",
      body: JSON.stringify({ body }),
    }),

  likeClientTopicMessage: (topicId: string, messageId: string) =>
    portalFetch<{ liked: boolean; count: number; likers: string[] }>(`/api/portal/client-topics/${topicId}/messages/${messageId}/like`, {
      method: "POST",
    }),

  documents: () => portalFetch<PortalDocument[]>("/api/portal/documents"),

  documentDownloadUrl: (docId: string, mode: "view" | "download" = "download") =>
    portalFetch<{ downloadUrl: string }>(`/api/portal/documents/${docId}/download-url?mode=${mode}`),

  preferences: () =>
    portalFetch<{ timezone: string; notification_start_hour: number; notification_end_hour: number }>(
      "/api/portal/preferences"
    ),

  updatePreferences: (prefs: { timezone?: string; notification_start_hour?: number; notification_end_hour?: number }) =>
    portalFetch<{ timezone: string; notification_start_hour: number; notification_end_hour: number }>(
      "/api/portal/preferences",
      { method: "PUT", body: JSON.stringify(prefs) }
    ),

  deleteAttachment: (taskId: string, attachmentId: string) =>
    portalFetch<void>(`/api/portal/tasks/${taskId}/attachments/${attachmentId}`, { method: "DELETE" }),

  // Staff-facing invite management — use staff auth token, not portal token
  createInvite: (account_id: string, email: string) =>
    fetchWithAuth<{ id: string; email: string; invited_at: string; invite_url: string }>(
      "/api/portal/invites",
      { method: "POST", body: JSON.stringify({ account_id, email }) }
    ),

  listInvites: (account_id: string) =>
    fetchWithAuth<PortalInvite[]>(`/api/portal/invites?account_id=${account_id}`),

  revokeInvite: (id: string) =>
    fetchWithAuth<void>(`/api/portal/invites/${id}/revoke`, { method: "POST" }),

  deleteInvite: (id: string) =>
    fetchWithAuth<void>(`/api/portal/invites/${id}`, { method: "DELETE" }),
};
